import { createSlice, PayloadAction } from '@reduxjs/toolkit';

export interface OnboardingData {
  // Step 1: Signup Data
  email: string;
  password: string;
  fullName: string;
  acceptedPolicy: boolean;
  clerkUserId?: string;
  
  // Step 2: Pricing Data
  selectedPlan: string;
  planPrice?: number;
  planFeatures?: string[];
  
  // Step 3: Phone Verification Data
  phoneNumber: string;
  smsConsent: boolean;
  
  // Step 4: Payment & Account Creation Data
  paymentMethodId?: string;
  paymentMethod?: string;
  paymentStatus?: 'pending' | 'completed' | 'failed';
  stripePaymentIntentId?: string;
  stripeCustomerId?: string; // Added to store customer ID at the top level
  cardLast4?: string;
  cardBrand?: string;
  priceId?: string;
  profileCreated?: boolean;
  paidUserDataAdded?: boolean;
  paymentMethodData?: {
    stripePaymentMethodId: string;
    stripeCustomerId: string;
    cardDetails: {
      last4: string;
      brand: string;
      expMonth: number;
      expYear: number;
      cardholderName: string;
    };
  };
  
  // Metadata
  currentStep: number;
  completedSteps: boolean[];
  startedAt: string;
  updatedAt?: string;
  completedAt?: string;
}

const initialState: OnboardingData = {
  email: '',
  password: '',
  fullName: '',
  acceptedPolicy: false,
  selectedPlan: '',
  phoneNumber: '',
  smsConsent: false,
  currentStep: 1,
  completedSteps: [false, false, false, false],
  startedAt: new Date().toISOString(),
};

// No localStorage backup - removed persistence functionality

const onboardingSlice = createSlice({
  name: 'onboarding',
  initialState,
  reducers: {
    updateOnboardingData: (state, action: PayloadAction<Partial<OnboardingData>>) => {
      return {
        ...state,
        ...action.payload,
        updatedAt: new Date().toISOString(),
      };
    },
    
    updateStepData: (state, action: PayloadAction<{ step: number; data: Partial<OnboardingData> }>) => {
      const { step, data } = action.payload;
      const newCompletedSteps = [...state.completedSteps];
      newCompletedSteps[step - 1] = true;
      
      return {
        ...state,
        ...data,
        currentStep: step,
        completedSteps: newCompletedSteps,
        updatedAt: new Date().toISOString(),
      };
    },
    
    nextStep: (state, action: PayloadAction<Partial<OnboardingData> | undefined>) => {
      const nextStepNumber = Math.min(state.currentStep + 1, 4);
      const newCompletedSteps = [...state.completedSteps];
      newCompletedSteps[state.currentStep - 1] = true;
      
      return {
        ...state,
        ...(action.payload || {}),
        currentStep: nextStepNumber,
        completedSteps: newCompletedSteps,
        updatedAt: new Date().toISOString(),
      };
    },
    
    goToStep: (state, action: PayloadAction<number>) => {
      return {
        ...state,
        currentStep: action.payload,
        updatedAt: new Date().toISOString(),
      };
    },
    
    // New action for updating profile data without affecting payment information
    updateProfileData: (state, action: PayloadAction<{
      email?: string;
      fullName?: string;
      phoneNumber?: string;
      currentStep?: number;
    }>) => {
      return {
        ...state,
        ...action.payload,
        updatedAt: new Date().toISOString(),
      };
    },
    
    // New action to go back for editing while preserving payment data
    goBackToEditProfile: (state, action: PayloadAction<number>) => {
      return {
        ...state,
        currentStep: action.payload,
        // Reset account creation flags so user can retry
        profileCreated: false,
        paidUserDataAdded: false,
        // Keep payment data intact - don't reset paymentStatus, stripePaymentIntentId, etc.
        updatedAt: new Date().toISOString(),
      };
    },
    
    // Action to restore from localStorage backup - removed (no persistence)
    restoreFromBackup: (state) => {
      // No backup functionality - return current state
      return {
        ...state,
        updatedAt: new Date().toISOString(),
      };
    },
    
    completeOnboarding: (state) => {
      return {
        ...state,
        completedSteps: [true, true, true, true],
        completedAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      };
    },
    
    resetOnboarding: () => {
      // No backup to clear - just return fresh initial state
      return {
        ...initialState,
        startedAt: new Date().toISOString(),
      };
    },
    
    // New action to reset only when truly starting over (not for editing)
    resetForNewSignup: () => {
      // No backup to clear - just return fresh initial state
      return {
        ...initialState,
        startedAt: new Date().toISOString(),
      };
    },
  },
});

export const {
  updateOnboardingData,
  updateStepData,
  nextStep,
  goToStep,
  updateProfileData,
  goBackToEditProfile,
  restoreFromBackup,
  completeOnboarding,
  resetOnboarding,
  resetForNewSignup,
} = onboardingSlice.actions;

export default onboardingSlice.reducer;